import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type RefObject } from 'react';
import { Dialog as Base } from '@base-ui/react/dialog';
import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';
import { CANVAS_UI } from '../editor/canvas-ui';
import { formatCombo } from '../editor/ShortcutsHelp';
import { cn } from '../../ui/cn';
import { Icon } from '../../ui/icons';
import { Kbd } from '../../ui/Kbd';
import { Input } from '../../ui/Field';
import { rankCommands, type PaletteCommand } from './commands';

/**
 * ⌘K palette (P11): a modal combobox over the commands of the current screen. The chosen command runs once the
 * palette has closed and given the focus back, so a dialog it opens keeps the focus.
 */
export function CommandPalette({ commands, open, onClose }: { commands: readonly PaletteCommand[]; open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const pending = useRef<PaletteCommand | null>(null);
  const input = useRef<HTMLInputElement>(null);
  return (
    <Base.Root
      open={open}
      onOpenChange={(next) => !next && onClose()}
      onOpenChangeComplete={(next) => {
        if (next) return;
        const chosen = pending.current;
        pending.current = null;
        chosen?.run();
      }}
    >
      <Base.Portal>
        <Base.Backdrop className="os-fade fixed inset-0 z-50 bg-overlay" />
        <Base.Popup
          {...{ [CANVAS_UI]: '' }}
          initialFocus={input}
          className="os-pop fixed left-1/2 top-[12dvh] z-50 flex max-h-[70dvh] w-[calc(100vw-2rem)] max-w-lg -translate-x-1/2 flex-col overflow-hidden rounded-3xl bg-chrome font-sans text-sm text-fg shadow-float outline-none"
        >
          <Base.Title className="sr-only">{t('commands.palette')}</Base.Title>
          <PaletteBody
            commands={commands}
            inputRef={input}
            onChoose={(command) => {
              pending.current = command;
              onClose();
            }}
          />
        </Base.Popup>
      </Base.Portal>
    </Base.Root>
  );
}

/** Mounted with the popup: the query and the active option start over at each opening. */
function PaletteBody({ commands, inputRef, onChoose }: { commands: readonly PaletteCommand[]; inputRef: RefObject<HTMLInputElement | null>; onChoose: (c: PaletteCommand) => void }) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const listId = useId();
  const results = useMemo(() => rankCommands(query, commands), [query, commands]);
  const current = Math.min(active, results.length - 1);
  const optionId = (i: number) => `${listId}-${i}`;

  useEffect(() => {
    document.getElementById(`${listId}-${current}`)?.scrollIntoView?.({ block: 'nearest' });
  }, [listId, current]);

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (results.length) setActive((current + (e.key === 'ArrowDown' ? 1 : -1) + results.length) % results.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const chosen = results[current];
      if (chosen) onChoose(chosen);
    }
  };

  // Without a query the commands keep their order, under their group's heading; ranked results are one list.
  const groups: { group: PaletteCommand['group'] | null; items: { command: PaletteCommand; index: number }[] }[] = [];
  results.forEach((command, index) => {
    const group = query.trim() ? null : command.group;
    const last = groups.at(-1);
    if (last && last.group === group) last.items.push({ command, index });
    else groups.push({ group, items: [{ command, index }] });
  });

  return (
    <>
      <div className="flex items-center gap-2 border-b border-border px-4">
        <Icon icon={Search} size={18} className="shrink-0 text-fg-subtle" />
        <Input
          bare
          ref={inputRef}
          role="combobox"
          aria-expanded="true"
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={results.length ? optionId(current) : undefined}
          aria-label={t('commands.palette')}
          placeholder={t('palette.placeholder')}
          autoComplete="off"
          spellCheck={false}
          className="min-w-0 flex-1"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setActive(0);
          }}
          onKeyDown={onKeyDown}
        />
      </div>
      <div id={listId} role="listbox" aria-label={t('commands.palette')} className="overflow-y-auto p-2">
        {groups.map(({ group, items }) => (
          <div key={`${group ?? 'results'}-${items[0]!.index}`} role="group" aria-label={group ? t(`palette.groups.${group}`) : undefined}>
            {group && (
              <p aria-hidden="true" className="px-2 pb-1 pt-2 font-mono text-[11px] uppercase tracking-wider text-fg-subtle-text">
                {t(`palette.groups.${group}`)}
              </p>
            )}
            {items.map(({ command, index }) => (
              <div
                key={command.id}
                id={optionId(index)}
                role="option"
                aria-selected={index === current}
                className={cn(
                  'flex cursor-pointer items-center justify-between gap-3 rounded-md px-2 py-2 text-fg pointer-coarse:min-h-11',
                  index === current && 'bg-surface-2',
                )}
                onMouseDown={(e) => e.preventDefault()} // the input keeps the focus
                onMouseMove={() => index !== current && setActive(index)}
                onClick={() => onChoose(command)}
              >
                <span className="min-w-0 truncate">{command.label}</span>
                {command.shortcut && <Kbd className="shrink-0">{formatCombo(command.shortcut)}</Kbd>}
              </div>
            ))}
          </div>
        ))}
        {results.length === 0 && <p className="px-2 py-6 text-center text-fg-muted">{t('palette.empty')}</p>}
      </div>
    </>
  );
}
